import React, { useCallback, useEffect, useState } from 'react';

import Head from 'next/head';
import Image from 'next/image';
import { useRouter } from 'next/router';

import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Hidden,
  IconButton,
  Step,
  StepButton,
  Stepper,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';

import { styled } from '@mui/material/styles';

import LocationOnIcon from '@mui/icons-material/LocationOn';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import EventIcon from '@mui/icons-material/Event';
import PhoneIcon from '@mui/icons-material/Phone';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import HelpIcon from '@mui/icons-material/Help';

import { Formik, Form, Field } from 'formik';
import { TextField as FormikTextField } from 'formik-mui';
import * as Yup from 'yup';

import { format, addWeeks, parseISO } from "date-fns";

import cars from '../constants/cars';

import TopNav from '../components/TopNav';
import BottomNav from '../components/BottomNav';

import ServiceIcon from '../components/ServiceIcon';

import Link from '../components/Link';
import WelcomeSteps from '../components/quote-requests/WelcomeSteps';
import ChooseCar from '../components/quote-requests/ChooseCar';
import ChooseService from '../components/quote-requests/ChooseService';
import CostEstimate from '../components/quote-requests/CostEstimate';

import Where from '../components/quote-requests/Where';
import When from '../components/quote-requests/When';
import CarCard from '../components/quote-requests/CarCard';
import CarCardMobile from '../components/quote-requests/CarCardMobile';

import Onboard from '../components/auth/Onboard';
import axiosInstance from '../lib/axiosInstance';
import gaEvent from '../lib/gaEvent';

import useAuth from '../contexts/auth';


const StepperDark = styled(Stepper)(({ theme }) => ({
  '& .MuiStepLabel-label.Mui-active': {
    color: theme.palette.primary.light,
  },
  '& .MuiStepIcon-root.Mui-active': {
    color: theme.palette.primary.light,
  },
}))


const StepperLight = styled(Stepper)(({ theme }) => ({
  '& .MuiStepLabel-label': {
    color: '#fff',
  },
  '& .MuiStepLabel-label.Mui-active': {
    color: '#fff',
  },
  '& .MuiStepLabel-label.Mui-completed': {
    color: '#fff',
  },
  '& .MuiStepIcon-root': {
    color: 'white',
  },
  '& .MuiStepIcon-text': {
    color: theme.palette.primary.main,
    fill: theme.palette.primary.main,
  },
  '& .MuiStepIcon-text.Mui-active': {
    color: 'white',
  },
  '& .MuiStepIcon-root.Mui-active': {
    color: theme.palette.primary.light,
  },
  '& .MuiStepIcon-root.Mui-completed': {
    color: 'white',
  },
}))


const RequestQuotes = () => {
  const theme = useTheme();
  const router = useRouter();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));

  const [maxStep, setMaxStep] = useState(-1);
  const [activeStep, setActiveStep] = useState(-1);

  const [car, setCar] = useState({ make: null, model: null, year: '', type: '', vin: '' });
  const [carSearchData, setCarSearchData] = useState({
    makes: cars,
    models: [],
    years: [],
    types: [],
  });
  const [carReady, setCarReady] = useState(false);

  const [service, setService] = useState(null);
  const [services, setServices] = useState([]);

  const [estimate, setEstimate] = useState(undefined);

  const [where, setWhere] = useState({
    center: {
      lat: -27,
      lng: 25,
    },
    address: null,
    fullAddress: null,
    distance: 15,
  });

  const [when, setWhen] = useState(addWeeks(new Date(), 1));
  const [phone, setPhone] = useState('');
  const [description, setDescription] = useState('');

  const [initVin, setInitVin] = useState('');
  const [initPhone, setInitPhone] = useState('');

  const [authOpened, setAuthOpened] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [posting, setPosting] = React.useState(false);

  const [initLoad, setInitLoad] = useState(true);

  const { isAuthenticated } = useAuth();

  const saveRequestProgress = step => {
    if (!initLoad) {
      const requestProgress = {
        maxStep: step,
        car,
        carSearchData,
        carReady,
        service,
        services,
        where,
        when,
        phone,
        description,
      };
      localStorage.setItem('requestProgress', JSON.stringify(requestProgress));
    }
  };

  const handleNextStep = num => {
    if (maxStep < num) {
      setActiveStep(num);
      setMaxStep(num);
      saveRequestProgress(num);
    } else {
      setActiveStep(maxStep);
      saveRequestProgress(maxStep);
    }
  };

  const openAuth = () => {
    setSubmitting(true);
    if (isAuthenticated && !posting) {
      submitRequest();
    } else {
      setAuthOpened(true);
    }
  };

  const closeAuth = (event, reason, sent) => {
    if (sent) return;
    gaEvent({
      category: 'Request Quotes',
      action: 'Auth Closed',
      label: 'Auth Closed',
    });
    setAuthOpened(false);
    setSubmitting(false);
  };

  const setFieldValue = (_key, _value) => {
    // a bit redundant, added to reuse Onboard component
  };

  const submitRequest = email => {
    setPosting(true);

    const data = {
      make: car.make,
      model: car.model,
      year: car.year || null,
      engine: car.type,
      vin: car.vin,
      job: service.name,
      address: where.fullAddress,
      distance: where.distance,
      lat: where.center.lat,
      lng: where.center.lng,
      when: format(when, 'eeee dd MMMM yyyy'),  // would be better ISO
      phone,
      description,
      email,
    };

    axiosInstance
      .post(`/api/v2/create-job/`, data)
      .then(() => {
        gaEvent({
          category: 'Request Quotes',
          action: 'Request Created',
          label: 'Request Created',
        });
        localStorage.removeItem('requestProgress');
        if (!email) {
          window.location = '/dash/';
        }
      })
      .catch(error => {
        setSubmitting(false);
        setPosting(false);
        console.log('error', error);
        gaEvent({
          category: 'Request Quotes',
          action: 'Request Failed',
          label: 'Request Failed',
        });
      });
  };

  useEffect(() => {
    const loadRequestProgress = () => {
      const rp = JSON.parse(localStorage.getItem('requestProgress'));
      let query = window.location.search;
      if (rp) {
        if (localStorage.getItem('personalQuote') || localStorage.getItem('hooked')) {
          setActiveStep(0);
          setMaxStep(1);
        } else if (rp.maxStep) {
          setMaxStep(rp.maxStep);
          if (query) {
            query = query.replace('?step=', '');
            if (query * 1 <= rp.maxStep) {
              setActiveStep(query * 1);
            } else {
              setActiveStep(rp.maxStep);
            }
          } else {
            setActiveStep(rp.maxStep);
          }
        }
        if (rp.car) {
          setCar(rp.car);
          setInitVin(rp.car.vin);
        }
        if (rp.carSearchData) setCarSearchData(rp.carSearchData);
        if (rp.carReady) setCarReady(rp.carReady);
        if (rp.service) setService(rp.service);
        if (rp.services) setServices(rp.services);
        if (rp.where) setWhere(rp.where);
        if (rp.when) setWhen(parseISO(rp.when));
        if (rp.phone) {
          setPhone(rp.phone);
          setInitPhone(rp.phone);
        }
        if (rp.description) setDescription(rp.description);
      } else if (query) {
        router.push(`/request-quotes`);
      }
      setTimeout(() => {
        setInitLoad(false);
      }, 1000);
    };

    loadRequestProgress();

    (async () => {
      try {
        const result = await axiosInstance('/content/services/?page_size=1000');
        setServices(result.data.results);
      } catch (error) {
        console.log('services error', error);
      }
    })();
  }, []);

  const getEstimate = useCallback(
    () => {
      if (car.make && car.model && service) {
        setEstimate(undefined);
        axiosInstance('/content/cost-estimate/', {
          params: {
            car: car.make,
            model: car.model,
            service: service.name,
          },
        }).then(result => {
          setEstimate(result.data);
        }).catch(error => {
          if (error?.response?.status === 404) {
            setEstimate(null);
          } else {
            console.log('estimate error', error);
          }
        })
      }
    },
    [car, service],
  );

  useEffect(() => {
    if (service && activeStep === 2) {
      getEstimate();
      saveRequestProgress(maxStep);
    }
  }, [service, activeStep, getEstimate]);

  useEffect(() => {
    if (where.address) {
      saveRequestProgress(maxStep);
    }
  }, [where.address]);

  useEffect(() => {
    if (description) {
      saveRequestProgress(maxStep);
    }
  }, [description]);

  useEffect(() => {
    if (car.vin) {
      saveRequestProgress(maxStep);
    }
  }, [car.vin]);

  useEffect(() => {
    if (phone) {
      saveRequestProgress(maxStep);
    }
  }, [phone]);

  useEffect(() => {
    if (activeStep !== -1 && router.query.step * 1 !== activeStep) {
      router.push(`?step=${activeStep}`, undefined, { shallow: true });
    }
  }, [activeStep]);

  useEffect(() => {
    if (router.query.step !== undefined && router.query.step * 1 !== activeStep) {
      if (router.query.step * 1 <= maxStep) {
        setActiveStep(router.query.step * 1);
      } else if (maxStep !== 1) {
        if (!localStorage.getItem('personalQuote') && !localStorage.getItem('hooked')) {
          setActiveStep(maxStep);
        }
      } else {
        router.push(`/request-quotes`);
      }
    }
  }, [router.query.step]);

  const SummaryCardAttribute = ({ icon, text, stepNum }) => {
    
    return (
      <Box
        display="flex"
        alignItems="center"
        mt={3}
        onClick={() => {
          if (stepNum === 'phone') {
            setPhone(null);
          } else {
            setActiveStep(stepNum);
          }
        }}
      >
        <Box display="flex" alignItems="center" color="primary.light">
          {icon}
        </Box>
        <Box ml={1} overflow="hidden" whiteSpace="nowrap">
          <Typography
            sx={{
              cursor: 'pointer',
              '&:hover': {
                color: 'secondary.main',
              },
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {text}
          </Typography>
        </Box>
      </Box>
    );
  };

  let bgImg = 'choose-car.jpg';
  let isDark = false;
  let headingColor = '';

  switch (activeStep) {
    case -1:
      bgImg = 'welcome-steps.jpg';
      isDark = true;
      headingColor = { xs: 'primary.main', md: '#fff' };
      break;

    case 0:
      bgImg = 'choose-car.jpg';
      isDark = true;
      headingColor = { xs: 'primary.main', md: '#fff' };
      break;

    case 1:
      bgImg = 'choose-service.jpg';
      isDark = true;
      headingColor = { xs: 'primary.main', md: '#fff' };
      break;

    case 2:
      bgImg = 'choose-service.jpg';
      isDark = true;
      headingColor = { xs: 'primary.main', md: '#fff' };
      break;

    case 3:
      bgImg = 'choose-location.jpg';
      isDark = false;
      headingColor = 'primary.main';
      break;

    case 4:
      bgImg = '';
      isDark = false;
      headingColor = 'primary.main';
      break;

    case 5:
      bgImg = 'quote-summary.jpg';
      isDark = true;
      headingColor = { xs: 'primary.main', md: '#fff' };
      break;

    default:
      break;
  }

  const [vinHelpDialog, setVinHelpDialog] = useState(false);

  const BoostSchema = Yup.object().shape({
    vin: Yup.string().test('length', 'Vin must be 17 characters', val =>
      val ? val.toString().length === 17 : true
    ),
    phone: Yup.string().min(10, 'Please enter a valid mobile number'),
  });

  return <>
    <Head>
      <title>Service and Repair Quotes - MechanicBuddy</title>
      <meta
        name="description"
        content="Easy car service and repair cost estimates in a few quick steps. No need to go to them, have the best local mechanics contact you."
      />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://www.mechanicbuddy.co.za/request-quotes/" />
      <meta property="og:title" content="Easy car service and repair cost estimates" />
      <meta
        property="og:description"
        content="Get service and repair quotes in a few quick steps. No need to go to them, have the best local mechanics contact you."
      />
      <meta property="og:image" content="https://www.mechanicbuddy.co.za/img/welcome-steps.jpg" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary" />
      <meta property="twitter:url" content="https://www.mechanicbuddy.co.za/request-quotes/" />
      <meta property="twitter:title" content="Easy car service and repair cost estimates" />
      <meta
        property="twitter:description"
        content="Get service and repair quotes in a few quick steps. No need to go to them, have the best local mechanics contact you."
      />
      <meta
        property="twitter:image"
        content="https://www.mechanicbuddy.co.za/img/welcome-steps.jpg"
      />

      <link rel="canonical" href="https://www.mechanicbuddy.co.za/request-quotes/" />
    </Head>

    <Box display="flex" flexDirection="column" minHeight="100vh">
      <TopNav />

      <Box
        flex={1}
        sx={{
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          display: {
            xs: 'block',
            md: 'flex',
          },
          backgroundImage: {
            md: bgImg
            ? `linear-gradient(to right, ${
                isDark
                  ? 'rgba(19, 53, 70, 0.6), rgba(19, 53, 70, 0.6)'
                  : 'rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.5)'
              }), url("/img/${bgImg}")`
            : 'unset',
          }
        }}
      >
        <Container
          sx={{
            py: {
              xs: 3,
              md: 8
            },
            alignSelf: activeStep === -1 || (activeStep === 0 && !car?.make) ? 'center' : 'unset',
          }}
        >
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
            <Hidden mdDown>
              {activeStep !== -1 && maxStep < 3 && car?.make && (
                <Box width={600} m="0 auto" mb={5}>
                  <StepperLight activeStep={activeStep}>
                    <Step>
                      <StepButton onClick={() => setActiveStep(0)}>
                        {car?.make ? car.make : 'Select a car'}
                      </StepButton>
                    </Step>
                    <Step>
                      <StepButton onClick={() => setActiveStep(1)}>Service</StepButton>
                    </Step>
                    <Step>
                      <StepButton
                        onClick={() => {
                          getEstimate();
                          setActiveStep(2);
                        }}
                      >
                        Estimate
                      </StepButton>
                    </Step>
                  </StepperLight>
                </Box>
              )}
              {maxStep >= 3 && (
                <Box width="100%" mb={5}>
                  { isDark ?
                    <StepperLight activeStep={activeStep}>
                      <Step>
                        <StepButton onClick={() => setActiveStep(0)}>
                          {car?.make ? car.make : 'Select a car'}
                        </StepButton>
                      </Step>
                      <Step>
                        <StepButton onClick={() => setActiveStep(1)}>Services</StepButton>
                      </Step>
                      <Step>
                        <StepButton
                          onClick={() => {
                            getEstimate();
                            setActiveStep(2);
                          }}
                        >
                          Estimate
                        </StepButton>
                      </Step>
                      <Step>
                        <StepButton onClick={() => setActiveStep(3)}>Where</StepButton>
                      </Step>
                      <Step>
                        <StepButton onClick={() => setActiveStep(4)}>When</StepButton>
                      </Step>
                      <Step>
                        <StepButton onClick={() => setActiveStep(5)}>Done</StepButton>
                      </Step>
                    </StepperLight> : 
                    <StepperDark activeStep={activeStep}>
                      <Step>
                        <StepButton onClick={() => setActiveStep(0)}>
                          {car?.make ? car.make : 'Select a car'}
                        </StepButton>
                      </Step>
                      <Step>
                        <StepButton onClick={() => setActiveStep(1)}>Services</StepButton>
                      </Step>
                      <Step>
                        <StepButton
                          onClick={() => {
                            getEstimate();
                            setActiveStep(2);
                          }}
                        >
                          Estimate
                        </StepButton>
                      </Step>
                      <Step>
                        <StepButton onClick={() => setActiveStep(3)}>Where</StepButton>
                      </Step>
                      <Step>
                        <StepButton onClick={() => setActiveStep(4)}>When</StepButton>
                      </Step>
                      <Step>
                        <StepButton onClick={() => setActiveStep(5)}>Done</StepButton>
                      </Step>
                    </StepperDark>
                  }
                </Box>
              )}
            </Hidden>
            {activeStep === -1 && (
              <>
                <Box
                  color={headingColor}
                  textAlign="center"
                  maxWidth={1000}
                  mb={{ xs: 4, md: 6 }}
                >
                  <Typography variant="h3" component="h1" gutterBottom>
                    Need a cost estimate?
                  </Typography>

                  <Box color={{ xs: '#555', md: '#fff' }}>
                    <Typography variant="h5" component="h2">
                      Get quotes in a few quick steps
                    </Typography>
                  </Box>
                </Box>
                <WelcomeSteps handleNextStep={handleNextStep} />
              </>
            )}

            {activeStep === 0 && (
              <>
                <Box color={headingColor} textAlign="center" maxWidth={1000} mb={6}>
                  <Typography variant="h3" component="h1">
                    {car?.make ? 'Your car details' : 'Select your car'}
                  </Typography>
                </Box>
                <ChooseCar
                  handleNextStep={handleNextStep}
                  setActiveStep={setActiveStep}
                  car={car}
                  setCar={setCar}
                  carSearchData={carSearchData}
                  setCarSearchData={setCarSearchData}
                  carReady={carReady}
                  setCarReady={setCarReady}
                  getEstimate={getEstimate}
                />
              </>
            )}
            {activeStep === 1 && (
              <>
                <Box color={headingColor} textAlign="center" maxWidth={1000} mb={6}>
                  <Typography variant="h3" component="h1">
                    What service or repairs do you require?
                  </Typography>
                </Box>
                <ChooseService
                  handleNextStep={handleNextStep}
                  services={services}
                  service={service}
                  setService={setService}
                />
              </>
            )}
            {activeStep === 2 || activeStep === 3 ? (
              <>
                {activeStep === 3 && !where.address && (
                  <Hidden mdUp>
                    <Box color={headingColor} textAlign="center" maxWidth={1000} mb={6}>
                      <Typography variant="h3" component="h1">
                        Where are you?
                      </Typography>

                      <Box color="#555" mt={2}>
                        <Typography variant="h5">
                          Indicate where the service should take place
                        </Typography>
                      </Box>
                    </Box>
                  </Hidden>
                )}
                <Box display="flex" flexDirection="column" width={{ xs: '100%', md: 'unset' }}>
                  {activeStep === 2 && (
                    <Box
                      display="flex"
                      flexDirection={{ xs: 'column', md: 'row' }}
                      alignItems={{ xs: 'center', md: 'unset' }}
                      width={{ xs: '100%', md: 'unset' }}
                    >
                      {!isMobile && <CarCard car={car} setCar={setCar} setActiveStep={setActiveStep} />}
                      <Box
                        width={{ xs: '100%', md: 325 }}
                        maxWidth={325}
                        height={estimate === undefined ? 455 : 'unset'}
                        minHeight={estimate ? 455 : 'unset'}
                      >
                        <Card
                          sx={{
                            width: '100%',
                            height: '100%',
                          }}
                          onClick={() => setActiveStep(1)}
                        >
                          <CardContent
                            sx={{
                              height: '100%',
                              pb: 2,
                            }}
                          >
                            {estimate === undefined && (
                              <Box
                                height="100%"
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                justifyContent="center"
                              >
                                <CircularProgress />
                                <Box mt={2}>Calculating estimate</Box>
                              </Box>
                            )}
                            {estimate !== undefined && (
                              <CostEstimate
                                service={service.name}
                                estimate={estimate}
                                setActiveStep={setActiveStep}
                              />
                            )}
                          </CardContent>
                        </Card>
                      </Box>
                      {isMobile && <CarCardMobile car={car} setActiveStep={setActiveStep} />}
                    </Box>
                  )}
                  {maxStep > 2 && activeStep === 2 ? (
                    <Box mt={4} textAlign="center">
                      <Button
                        variant="contained"
                        color="secondary"
                        size="large"
                        onClick={() => setActiveStep(maxStep)}
                      >
                        CONTINUE
                      </Button>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        width: "100%",
                        mt: activeStep === 2 ? 4 : 0,
                        alignSelf: "center",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        px: {
                          xs: 1,
                          md: 0
                        }
                      }}
                    >
                      {activeStep === 2 && !isMobile &&
                        <Box
                          color={headingColor}
                          mb={2}
                          textAlign="center"
                          fontSize="h2.fontSize"
                        >
                          <Box
                            component="span"
                            sx={{
                              fontWeight: 500,
                              color: {
                                xs: 'primary.light',
                                md: 'secondary.main',
                              }
                            }}
                          >Continue</Box> to get your own{' '}
                          <Box
                            component="span"
                            sx={{
                              fontWeight: 500,
                              color: {
                                xs: 'primary.light',
                                md: 'secondary.main',
                              }
                            }}
                          > quotes</Box>
                        </Box>
                      }
                      <Where
                        make={car.make}
                        service={service?.name}
                        handleNextStep={handleNextStep}
                        activeStep={activeStep}
                        where={where}
                        setWhere={setWhere}
                      />
                    </Box>
                  )}
                </Box>
              </>
            ) : null}
            {activeStep === 4 && (
              <>
                <Box
                  color={headingColor}
                  textAlign="center"
                  maxWidth={1000}
                  mb={{ xs: 4, md: 6 }}
                >
                  <Typography variant="h3" component="h1">
                    Choose a service date
                  </Typography>
                </Box>
                <Box display="flex" flexDirection="column" alignItems="center">
                  <Box width="fit-content">
                    <When date={when} setDate={setWhen} />
                  </Box>
                  <Box mt={2} textAlign="center">
                    <Typography variant="h5" component="h2">
                      Does{' '}
                      <Typography
                        variant="inherit"
                        component="span"
                        sx={{
                          color: 'primary.light',
                          fontWeight: 500,
                        }}
                      >
                        {`${format(when, 'eeee dd MMMM yyyy')}`}
                      </Typography>{' '}
                      suite you?
                    </Typography>
                  </Box>
                  <Box mt={4} textAlign="center">
                    <Button
                      variant="contained"
                      color="secondary"
                      size="large"
                      onClick={() => {
                        gaEvent({
                          category: 'Request Quotes',
                          action: 'Submit Date',
                          label: 'Choose When',
                        });
                        handleNextStep(5);
                      }}
                      disabled={!when}
                    >
                      Confirm
                    </Button>
                  </Box>
                </Box>
              </>
            )}
            {activeStep === 5 && (
              <>
                <Box
                  color={headingColor}
                  textAlign="center"
                  maxWidth={1000}
                  mb={{ xs: 4, md: 6 }}
                >
                  <Typography variant="h3" component="h1">
                    Request Summary
                  </Typography>
                </Box>
                <Box
                  width={{ xs: '100%', md: 'unset' }}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                >
                  <Box
                    display="flex"
                    flexDirection={{ xs: 'column', md: 'row' }}
                    alignItems={{ xs: 'center', md: 'unset' }}
                    width={{ xs: '100%', md: 'unset' }}
                  >
                    <Hidden mdDown>
                      <CarCard
                        car={car}
                        setCar={setCar}
                        setActiveStep={setActiveStep}
                        clickable
                      />
                    </Hidden>
                    <Box width={{ xs: '100%', md: 325 }} maxWidth={325}>
                      <Card
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          width: '100%',
                          height: '100%',
                        }}
                      >
                        <CardContent>
                          <Box
                            display="flex"
                            alignItems="center"
                            onClick={() => setActiveStep(1)}
                            style={{ cursor: 'pointer' }}
                          >
                            <ServiceIcon
                              service={service.category}
                              color={theme.palette.secondary.main}
                            />
                            <Box
                              sx={{
                                ml: 1,
                                cursor: 'pointer',
                                '&:hover': {
                                  color: 'secondary.main',
                                },
                              }}
                            >
                              <Typography variant="h6">{service.name}</Typography>
                            </Box>
                          </Box>
                          <Box mt={3}>
                            <TextField
                              multiline
                              label="Description"
                              placeholder={service.helper || 'Please provide more details...'}
                              defaultValue={description}
                              onBlur={event => setDescription(event.target.value)}
                              minRows={4}
                              maxRows={4}
                              variant="outlined"
                              fullWidth
                              autoFocus={!description}
                              autoComplete="off"
                            />
                          </Box>
                          <SummaryCardAttribute
                            icon={<LocationOnIcon color="inherit" />}
                            text={where.fullAddress}
                            stepNum={3}
                          />
                          <SummaryCardAttribute
                            icon={<TrackChangesIcon color="inherit" />}
                            text={`${where.distance} km`}
                            stepNum={3}
                          />
                          <SummaryCardAttribute
                            icon={<EventIcon color="inherit" />}
                            text={`${format(when, 'eeee dd MMMM yyyy')}`}
                            stepNum={4}
                          />
                          {phone && (
                            <SummaryCardAttribute
                              icon={<PhoneIcon color="inherit" />}
                              text={phone}
                              stepNum="phone"
                            />
                          )}
                        </CardContent>
                      </Card>
                    </Box>
                    <Hidden mdUp>
                      <CarCardMobile
                        car={car}
                        setActiveStep={setActiveStep}
                        isSummary
                        setCar={setCar}
                      />
                    </Hidden>
                  </Box>
                  {!car.vin || !phone ? (
                    <Box
                      bgcolor="primary.main"
                      width="100%"
                      maxWidth={{ xs: 325, md: 'unset' }}
                      mt={2}
                      borderRadius="4px"
                      p={2}
                      color="#fff"
                    >
                      <Box mb={2} textAlign="center">
                        <Typography variant="h6">
                          Provide more info and boost your quotes
                        </Typography>
                      </Box>
                      <Formik
                        initialValues={{
                          vin: initVin,
                          phone: initPhone,
                        }}
                        onSubmit={(values) => {
                          if (values.vin) {
                            setInitVin(values.vin);
                            setCar({ ...car, vin: values.vin });
                          }
                          if (values.phone) {
                            setInitPhone(values.phone);
                            setPhone(values.phone);
                          }
                          saveRequestProgress(maxStep);
                        }}
                        validationSchema={BoostSchema}
                      >
                        {({ isValid, dirty }) => {
                          let isDirty = dirty;
                          if ((Boolean(initVin) && !car.vin) || (Boolean(initPhone) && !phone)) {
                            isDirty = true;
                          }
                          return (
                            <Form>
                              <Box
                                display="flex"
                                flexDirection={{ xs: 'column', md: 'row' }}
                                height={{ xs: 'unset', md: 56 }}
                              >
                                {!car.vin && (
                                  <Box
                                    flex={1}
                                    mr={{ xs: 0, md: 2 }}
                                    mb={{ xs: 1, md: 0 }}
                                    position="relative"
                                    display="flex"
                                    alignItems="flex-start"
                                  >
                                    <Field
                                      component={FormikTextField}
                                      name="vin"
                                      label="VIN"
                                      variant="filled"
                                      color="secondary"
                                      fullWidth
                                      autoComplete="off"
                                      autoFocus={Boolean(initVin)}
                                      sx={{
                                        backgroundColor: '#fff',
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                      }}
                                    />
                                    <IconButton
                                      sx={{
                                        position: 'absolute',
                                        right: 4,
                                        alignSelf: 'center',
                                      }}
                                      onClick={() => setVinHelpDialog(true)}
                                      size="large">
                                      <HelpIcon fontSize="small" />
                                    </IconButton>
                                    <Dialog
                                      open={vinHelpDialog}
                                      onClose={() => setVinHelpDialog(false)}
                                    >
                                      <DialogTitle>
                                        What is a VIN and where do I find it?
                                      </DialogTitle>
                                      <DialogContent>
                                        <Box width="100%">
                                          <Typography gutterBottom>
                                            It is on your car&apos;s licence disc in your window.
                                          </Typography>
                                          <Image
                                            src="/img/license-disc.jpg"
                                            width={590}
                                            height={400}
                                            alt="Licence Disc"
                                            title="Licence Disc"
                                          />
                                        </Box>
                                      </DialogContent>
                                      <DialogActions>
                                        <Button
                                          variant="contained"
                                          color="secondary"
                                          onClick={() => setVinHelpDialog(false)}
                                        >
                                          Got it
                                        </Button>
                                      </DialogActions>
                                    </Dialog>
                                  </Box>
                                )}
                                {!phone && (
                                  <Box flex={1} mr={{ xs: 0, md: 2 }} mb={{ xs: 2, md: 0 }}>
                                    <Field
                                      component={FormikTextField}
                                      name="phone"
                                      label="Phone"
                                      variant="filled"
                                      color="secondary"
                                      fullWidth
                                      autoComplete="off"
                                      autoFocus={Boolean(initPhone)}
                                      sx={{
                                        backgroundColor: '#fff',
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                      }}
                                    />
                                  </Box>
                                )}
                                <Box alignSelf="center">
                                  <Button
                                    type="submit"
                                    variant="contained"
                                    color="secondary"
                                    size="large"
                                    startIcon={<FlashOnIcon />}
                                    sx={{
                                      height: '100%',
                                      bgcolor: 'primary.light',
                                      '&:hover': {
                                        backgroundColor: '#147380',
                                      },
                                      '&.Mui-disabled': {
                                        bgcolor: '#147380',
                                        color: 'lightgray',
                                      },
                                    }}
                                    disabled={!(isValid && isDirty)}
                                  >
                                    Boost
                                  </Button>
                                </Box>
                              </Box>
                            </Form>
                          );
                        }}
                      </Formik>
                    </Box>
                  ) : null}
                  <Box display="flex" justifyContent="center">
                    <Box mt={{ xs: 2, md: 4 }} textAlign="center" width={200}>
                      <Box
                        pb={2}
                        fontSize="10px"
                        sx={{
                          color: {
                            xs: 'text.secondary',
                            md: 'white',
                          }
                        }}
                      >
                        By continuing you agree to MechanicBuddy&apos;s{' '}
                        <Link
                          target="_blank"
                          href="/end-user-terms-and-conditions/"
                          sx={{
                            color: {
                              xs: 'primary.light',
                              md: 'secondary.main',
                            }
                          }}
                        >
                          Terms of Service
                        </Link>
                      </Box>

                      <Button
                        variant="contained"
                        color="secondary"
                        size="large"
                        disabled={submitting}
                        onClick={() => {
                          gaEvent({
                            category: 'Request Quotes',
                            action: 'Click Submit',
                            label: 'Submit Request',
                          });
                          openAuth();
                        }}
                        fullWidth
                      >
                        Submit
                      </Button>

                      {authOpened && (
                        <Onboard
                          open={authOpened}
                          handleClose={closeAuth}
                          setFieldValue={setFieldValue}
                          submitForm={submitRequest}
                          posting={posting}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </Container>
      </Box>
    </Box>

    {activeStep === 2 && (
      <Hidden mdUp>
        <Box
          sx={{
            bgcolor: 'rgba(19, 53, 70, 0.8)',
            position: 'sticky',
            bottom: '0',
            textAlign: 'center',
            color: 'white',
            p: 2,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box color="white" mb={2} textAlign="center" fontSize="h6.fontSize">
            <b>Continue</b> to get your own <b>quotes</b>
          </Box>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            fullWidth
            onClick={() => handleNextStep(3)}
          >
            CONTINUE
          </Button>
        </Box>
      </Hidden>
    )}

    <BottomNav />
  </>;
};

export default RequestQuotes;
